/**
 * Vague 22 — Composant d'alerte Métacognition pour Ultra Admin
 *
 * - Polling /api/workspace/metacognition/unread-count (toutes les 60s)
 * - Affiche un toast/popup discret en bas à droite si total_unread > 0
 * - L'écouteur de l'event noam:metacog-seen reset le badge après visite
 *
 * Le badge sidebar est exporté via le contexte `MetacogUnreadContext`
 * pour que SaaSLayout puisse l'afficher sur l'item de navigation.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Brain, X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const MetacogUnreadContext = createContext({ total: 0, improvements: 0, market: 0, refresh: () => {} });

export const useMetacogUnread = () => useContext(MetacogUnreadContext);

export function MetacogUnreadProvider({ isUltraAdmin, children }) {
  const [counts, setCounts] = useState({ total: 0, improvements: 0, market: 0 });

  const refresh = useCallback(async () => {
    if (!isUltraAdmin) return;
    try {
      const r = await api.get("/workspace/metacognition/unread-count");
      setCounts({
        total: r.data?.total_unread || 0,
        improvements: r.data?.improvements_unread || 0,
        market: r.data?.market_unread || 0,
      });
    } catch { /* silent */ }
  }, [isUltraAdmin]);

  useEffect(() => {
    if (!isUltraAdmin) return;
    refresh();
    const id = setInterval(refresh, 60_000);
    const onSeen = () => setCounts({ total: 0, improvements: 0, market: 0 });
    window.addEventListener("noam:metacog-seen", onSeen);
    return () => {
      clearInterval(id);
      window.removeEventListener("noam:metacog-seen", onSeen);
    };
  }, [isUltraAdmin, refresh]);

  return (
    <MetacogUnreadContext.Provider value={{ ...counts, refresh }}>
      {children}
    </MetacogUnreadContext.Provider>
  );
}

/**
 * Popup discret en bas à droite, affichée 1 fois par session si unread > 0.
 * Cliquable → /app/metacognition.
 */
export function MetacognitionLoginPopup({ basePath = "/app" }) {
  const { total, improvements, market } = useMetacogUnread();
  const [dismissed, setDismissed] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!total || shown) return;
    try {
      if (sessionStorage.getItem("noam:metacog-popup-shown") === "1") {
        setDismissed(true);
        return;
      }
      sessionStorage.setItem("noam:metacog-popup-shown", "1");
    } catch { /* silent */ }
    setShown(true);
    // auto-dismiss après 12s
    const timer = setTimeout(() => setDismissed(true), 12_000);
    return () => clearTimeout(timer);
  }, [total, shown]);

  if (!total || dismissed) return null;

  return (
    <div
      className="fixed bottom-5 right-5 z-[80] max-w-sm w-[calc(100%-2.5rem)] sm:w-96 animate-in slide-in-from-bottom-2 fade-in duration-300"
      data-testid="metacog-login-popup"
    >
      <div className="rounded-lg border border-[#A78BFA]/40 bg-[#0a0a0a]/95 backdrop-blur shadow-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-[#A78BFA]/15 border border-[#A78BFA]/30 flex items-center justify-center shrink-0">
            <Brain className="w-4 h-4 text-[#A78BFA]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#A78BFA]">Métacognition · Ultra Admin</div>
            <div className="text-sm font-semibold text-zinc-100 mt-0.5">
              Noam a détecté de nouveaux signaux
            </div>
            <div className="text-xs text-zinc-400 mt-1">
              {improvements > 0 && (
                <>{improvements} amélioration{improvements > 1 ? "s" : ""} suggérée{improvements > 1 ? "s" : ""}</>
              )}
              {improvements > 0 && market > 0 && " · "}
              {market > 0 && (
                <>{market} signal{market > 1 ? "ux" : ""} concurrentiel{market > 1 ? "s" : ""}</>
              )}
              {" cette semaine."}
            </div>
            <Link
              to={`${basePath}/metacognition`}
              onClick={() => setDismissed(true)}
              className="inline-flex items-center gap-1 text-xs text-[#A78BFA] hover:underline mt-2"
              data-testid="metacog-popup-link"
            >
              Voir le détail <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-zinc-500 hover:text-zinc-200 p-1 shrink-0"
            data-testid="metacog-popup-dismiss"
            aria-label="Fermer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
